import { makeAutoObservable } from "mobx";

import { addressFormToCreateData, addressFormToUpdateData } from "helpers/forms/address";
import { type AddressFormValue } from "helpers/forms/addressTypes";
import { type AddressClient, AddressKey } from "services/address/addressClient";
import { type AddressData } from "services/address/addressTypes";

export enum DirectorAddressKey {
  Main = "main",
  Previous = "previous",
}

type DirectorAddressState = Record<
  string,
  {
    [DirectorAddressKey.Main]?: AddressData;
    [DirectorAddressKey.Previous]?: AddressData;
    isCurrentAddressLessThanOneYear?: boolean;
  }
>;

export class DirectorAddressStore {
  private readonly _state: DirectorAddressState = {};

  constructor(private readonly _addressClient: AddressClient) {
    makeAutoObservable(this);
  }

  tryGetForDirector(
    directorId: string,
    addressKey: DirectorAddressKey = DirectorAddressKey.Main,
  ): AddressData | undefined {
    return this._state[directorId]?.[addressKey];
  }

  hasAddressForDirector(directorId: string): boolean {
    return !!this._state[directorId]?.[DirectorAddressKey.Main];
  }

  hasPreviousAddressForDirector(directorId: string): boolean {
    return !!this._state[directorId]?.[DirectorAddressKey.Previous];
  }

  setisCurrentAddressLessThanOneYear(directorId: string, isCurrentAddressLessThanOneYear: boolean): void {
    this._state[directorId] = {
      ...this._state[directorId],
      isCurrentAddressLessThanOneYear,
    };
  }

  getisCurrentAddressLessThanOneYear(directorId: string): boolean {
    return this._state[directorId]?.isCurrentAddressLessThanOneYear ?? false;
  }

  async loadForDirector(directorId: string, addressKey: DirectorAddressKey = DirectorAddressKey.Main): Promise<void> {
    const address = await this._addressClient.getAddress(
      addressKey === DirectorAddressKey.Main ? AddressKey.Director : AddressKey.DirectorPrevious,
      directorId,
    );

    if (address) {
      this._setAddressToState(directorId, addressKey, address);
      if (addressKey === DirectorAddressKey.Previous) {
        this.setisCurrentAddressLessThanOneYear(directorId, true);
      }
    }
  }

  async updateForDirector(
    directorId: string,
    addressForm: AddressFormValue,
    addressKey: DirectorAddressKey = DirectorAddressKey.Main,
  ): Promise<void> {
    const existedAddress = this.tryGetForDirector(directorId, addressKey);
    let address;

    if (existedAddress) {
      const updatedAddressForm = addressFormToUpdateData(addressForm);
      address = await this._addressClient.updateAddress(existedAddress.id, updatedAddressForm);
    } else {
      const ids =
        addressKey === DirectorAddressKey.Main
          ? { directorIdRegistered: directorId }
          : { directorIdRegisteredPrevious: directorId };
      address = await this._addressClient.createAddress(addressFormToCreateData(addressForm, ids));
    }

    this._setAddressToState(directorId, addressKey, address);
  }

  clearPreviousAddressForDirector(directorId: string): void {
    this._state[directorId] = {
      ...this._state[directorId],
      [DirectorAddressKey.Previous]: undefined,
      isCurrentAddressLessThanOneYear: false,
    };
  }

  private _setAddressToState(directorId: string, addressKey: DirectorAddressKey, address: AddressData): void {
    this._state[directorId] = {
      ...this._state[directorId],
      [addressKey]: address,
    };
  }
}
